"use client";

import { CheckCircle2, FileText, Sparkles, Tag } from "lucide-react";
import { useState } from "react";

import { ProductReviews } from "@/components/product/product-reviews";
import type { Product, ReviewItem } from "@/types";

const tabs = ["Description", "Specifications", "Reviews", "Q&A"] as const;

export function ProductTabs({ product, reviews }: { product: Product; reviews: ReviewItem[] }) {
  const [active, setActive] = useState<(typeof tabs)[number]>("Description");

  const specs: Record<string, string> = product.specifications ?? {
    Brand: product.brand,
    Category: product.category,
    "Item model": product.slug,
    Warranty: product.warranty ?? "1 Year Manufacturer Warranty",
    Seller: product.seller ?? "MOVA Market Fulfilment"
  };

  // Parse paragraphs and bullet highlights
  const rawParagraphs = (product.description || "")
    .split(/\r?\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

  const featureBullets: string[] = [];
  rawParagraphs.forEach((para) => {
    if (/^[•\-\*]/.test(para)) {
      featureBullets.push(para.replace(/^[•\-\*]\s*/, ""));
    } else {
      const sentences = para
        .split(/(?<=[.!?])\s+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 20);

      if (sentences.length > 1) {
        featureBullets.push(...sentences);
      } else if (para.length > 0) {
        featureBullets.push(para);
      }
    }
  });

  return (
    <section>
      <div className="flex gap-0 overflow-x-auto border-b border-slate-200 dark:border-white/10 no-scrollbar">
        {tabs.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActive(tab)}
            className={`shrink-0 border-b-2 px-4 py-3 text-sm font-bold transition ${
              active === tab
                ? "border-amazon-orange text-amazon-orange"
                : "border-transparent text-slate-600 hover:text-slate-900 dark:text-slate-400"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {active === "Description" && (
          <div className="space-y-6">
            {/* Overview Section */}
            <div className="max-w-4xl">
              <h3 className="flex items-center gap-2 text-base font-bold text-slate-900 dark:text-white">
                <FileText className="h-4 w-4 text-amazon-orange" />
                Product Overview
              </h3>
              <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                {rawParagraphs.length > 0 ? (
                  rawParagraphs.map((para, idx) => (
                    <p key={idx} className="leading-7">
                      {para}
                    </p>
                  ))
                ) : (
                  <p className="italic text-slate-500">No description available for this item.</p>
                )}
              </div>
            </div>

            {/* Key Features & Highlights */}
            {featureBullets.length > 0 && (
              <div className="max-w-4xl rounded-xl border border-slate-200/80 bg-slate-50/60 p-4.5 dark:border-white/10 dark:bg-slate-800/40">
                <h4 className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                  <Sparkles className="h-4 w-4 text-amazon-teal" />
                  Key Highlights & Capabilities
                </h4>
                <ul className="mt-3.5 grid gap-2.5 sm:grid-cols-2">
                  {featureBullets.map((bullet, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-2.5 rounded-lg border border-slate-200/60 bg-white p-3 text-xs leading-relaxed text-slate-800 shadow-xs dark:border-white/5 dark:bg-slate-900/60 dark:text-slate-200 sm:text-sm"
                    >
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-amazon-teal" />
                      <span>{bullet.replace(/^[•\-\*]\s*/, "")}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Product Tags / Keywords */}
            {product.tags && product.tags.length > 0 && (
              <div className="max-w-4xl border-t border-slate-100 pt-4 dark:border-white/10">
                <h4 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  <Tag className="h-3.5 w-3.5" />
                  Product Tags & Keywords
                </h4>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  {product.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center rounded-md border border-slate-200 bg-slate-100/80 px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-200 dark:border-white/10 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-750"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {active === "Specifications" && (
          <div className="overflow-x-auto">
            <table className="w-full max-w-2xl text-sm">
              <tbody>
                {Object.entries(specs).map(([key, value]) => (
                  <tr key={key} className="border-b border-slate-100 dark:border-white/10">
                    <th className="py-3 pr-4 text-left font-bold text-slate-600 dark:text-slate-400 w-1/3 min-w-[100px] align-top">{key}</th>
                    <td className="py-3 text-slate-900 dark:text-white break-words align-top">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {active === "Reviews" && <ProductReviews product={product} initialReviews={reviews} />}

        {active === "Q&A" && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">Have a question? Search for answers.</p>
            {[
              { q: "Is this product genuine?", a: "Yes, verified by MOVA Market with genuine quality guarantee." },
              { q: "How does delivery work?", a: "Fast delivery across Addis Ababa and regional cities with real-time tracking." },
              { q: "Does Prime apply?", a: product.isPrime ? "Yes, eligible for Prime priority fast delivery." : "Standard delivery applies." }
            ].map((item) => (
              <article key={item.q} className="rounded border border-slate-200 p-4 dark:border-white/10">
                <p className="font-bold text-slate-950 dark:text-white">Q: {item.q}</p>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">A: {item.a}</p>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
